import React, { useState, useEffect } from 'react'
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from '@react-google-maps/api'
import { useAppState, useAppDispatch } from '../Context'
import { mapMarker, userMarker } from './icons'
import LocationCard from './LocationCard'

const mapOptions = {
  minZoom: 3,
  maxZoom: 15,
  fullscreenControl: false,
  mapTypeControl: false,
  streetViewControl: false,
  zoomControlOptions: {
    position: window.matchMedia('(min-width: 768px)').matches ? 8 : 9, // RIGHT_CENTER or RIGHT_BOTTOM
  },
}

function Map({ apiKey, initialCenter = null }) {
  const dispatch = useAppDispatch()
  const { locations, mapVisible, userCoords, selectedLocation } = useAppState()
  const [markers, setMarkers] = useState({})
  const [map, setMap] = useState()
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey,
  })

  useEffect(() => {
    if (selectedLocation) {
      map.panTo(selectedLocation.position)
      map.setZoom(8)
    }
  }, [selectedLocation, map])

  useEffect(() => {
    if (userCoords && map) {
      map.panTo(userCoords)

      if (map.getZoom() < 8) {
        map.setZoom(8)
      }
    }
  }, [userCoords, map])

  const onLoad = React.useCallback(
    function callback(map) {
      if (!initialCenter) {
        const bounds = new window.google.maps.LatLngBounds()

        locations.forEach((location) => {
          bounds.extend(location.position)
        })

        map.fitBounds(bounds)
      }

      setMap(map)
    },
    [locations]
  )

  const onUnmount = React.useCallback(function callback() {
    setMap(null)
  }, [])

  const markerLoadHandler = (marker, location) => {
    return setMarkers((prevState) => {
      return { ...prevState, [location.id]: marker }
    })
  }

  const markerClickHandler = (_, location) => {
    if (selectedLocation === location) {
      setSelectedLocation(null)
    } else {
      setSelectedLocation(location)
    }
  }

  const markerMouseOverHandler = (_, location) => {
    const marker = markers[location.id]
    marker.setZIndex(10)
  }

  const markerMouseOutHandler = (_, location) => {
    const marker = markers[location.id]
    marker.setZIndex(1)
  }

  const setSelectedLocation = (location) => {
    dispatch({ type: 'setSelectedLocation', payload: location })
  }

  if (loadError) {
    return <div>Map cannot be loaded right now.</div>
  }

  return (
    <div className={`locations-map-container ${mapVisible ? 'locations-map-container--visible' : ''}`}>
      {isLoaded ? (
        <GoogleMap
          mapContainerClassName="w-full h-full"
          options={mapOptions}
          zoom={8}
          onLoad={onLoad}
          onUnmount={onUnmount}
        >
          {locations.map((location) => (
            <Marker
              key={location.id}
              position={location.position}
              zIndex={1}
              icon={mapMarker}
              onLoad={(marker) => markerLoadHandler(marker, location)}
              onClick={(event) => markerClickHandler(event, location)}
              onMouseOver={(event) => markerMouseOverHandler(event, location)}
              onMouseOut={(event) => markerMouseOutHandler(event, location)}
            />
          ))}

          {userCoords && (
            <Marker position={userCoords} zIndex={0} icon={userMarker} animation={google.maps.Animation.DROP} />
          )}

          {selectedLocation && (
            <InfoWindow
              anchor={markers[selectedLocation.id]}
              position={selectedLocation.position}
              onCloseClick={() => setSelectedLocation(null)}
            >
              <LocationCard
                location={selectedLocation}
                asInfoWindow={true}
                className="p-4 xl:p-5 lg:min-w-[400px] xl:min-w-[440px]"
              />
            </InfoWindow>
          )}
        </GoogleMap>
      ) : (
        <div>Loading...</div>
      )}
    </div>
  )
}

export default React.memo(Map)
